export const STORAGE_KEY = 'sohozkaj.cashMemoDesigner.v1';


/**
 * Six printed cash-memo pads — the blank books a shop orders from the press,
 * each a measured copy of a real block. The tool prints the FORM, not a filled
 * sale: the shopkeeper writes the customer, the goods and the amounts in by
 * hand.
 */
export const TEMPLATES = [
    'memoClassic', 'memoBadge', 'memoSplit', 'memoOutline', 'memoShop', 'memoRuled',
];

/**
 * Ink colours for the memo — twenty, so with the free picker they fill four
 * even rows of five. The first is the press green of the classic pad. Each has to stay legible as a heading and as a thin rule
 * on white paper, so the palette is the darker half of the site's swatches.
 */
export const ACCENT_PRESETS = [
    '#017448', '#E68A00', '#F97316', '#FF5722', '#DC2626', '#E91E8C',
    '#9C27B0', '#7C3AED', '#673AB7', '#3F51B5', '#2563EB',
    '#0284C7', '#0891B2', '#0F766E', '#059669', '#2E7D32',
    '#65A30D', '#A16207', '#795548', '#111827',
];

/**
 * Paper the pads are cut to, in CSS pixels at 96 dpi. A4 is the sheet the press
 * prints; most shops then get it guillotined into halves or quarters, which is
 * why those are offered as their own sizes rather than as a "scale" setting.
 */
export const PAPERS = {
    a4: { width: 794, height: 1123 },
    a5: { width: 559, height: 794 },
    halfA4: { width: 794, height: 561 },
    a6: { width: 397, height: 559 },
};
export const PAPER_IDS = Object.keys(PAPERS);
export const paperSize = (id) => PAPERS[id] || PAPERS.a4;

/** How many blank lines the grid can be given. */
/** What the watermark can be: nothing, the shop's name, or a picture. */
export const WATERMARK_MODES = ['none', 'text', 'image'];

export const MIN_ROWS = 4;
export const MAX_ROWS = 30;
export const MAX_COLUMNS = 7;
export const MIN_COLUMNS = 2;

/** The one face the pad is printed in — Bangla and English both. */
export const MEMO_FONT_STACK = '"Hind Siliguri", "Noto Sans Bengali", "Anek Bangla", "Segoe UI", system-ui, sans-serif';

/**
 * The press colours of the classic pad — the cream stock, the green of the
 * address band and the column heads, the red of the shop name and the নাম /
 * ঠিকানা tags, and the lime of the signature strip. They are the design, not a
 * theme, so only the green follows the chosen ink.
 */
export const CLASSIC_COLORS = {
    paper: '#FFF3BF',
    green: '#017448',
    red: '#ED1C24',
    lime: '#D6DA22',
    limeInk: '#4A4E14',
    field: '#F5F6F5',
    fieldLine: '#E3DFD1',
    rule: '#C9C091',
    ink: '#1C1815',
    soft: '#3A3522',
};

/**
 * The press colours of the shop-header pad: white stock washing down to a
 * yellow-green foot, the shop's name in green, the proprietor on a red plate,
 * a maroon address band, orange tags, a blue column head and a black grid.
 */
export const SHOP_COLORS = {
    paperTop: '#FFFFFF',
    paperMid: '#F5F8E0',
    paperFoot: '#D8E47E',
    name: '#00A751',
    plate: '#EF1C26',
    band: '#9C090D',
    tag: '#F6821F',
    head: '#0172BB',
    note: '#1766B4',
    rule: '#161616',
    money: '#D8E1EE',
    ink: '#121212',
};

/** Which parts of the classic header are printed. */
export const CLASSIC_SHOW = ['art', 'logo', 'bismillah', 'plate', 'proprietor', 'phone', 'email', 'distributor', 'tagline', 'addressBand', 'slogan'];

/** The words printed on the memo itself — independent of the page language. */
export const DOC_LABELS = {
    bn: {
        title: 'ক্যাশ মেমো',
        memoNo: 'নম্বর',
        date: 'তারিখ',
        name: 'নাম',
        address: 'ঠিকানা',
        total: 'মোট',
        advance: 'অগ্রিম',
        due: 'বাকী',
        inWords: 'টাকা (কথায়)',
        customerSign: 'ক্রেতার স্বাক্ষর',
        sellerSign: 'বিক্রেতার স্বাক্ষর',
    },
    en: {
        title: 'Cash Memo',
        memoNo: 'No.',
        date: 'Date',
        name: 'Name',
        address: 'Address',
        total: 'Total',
        advance: 'Advance',
        due: 'Due',
        inWords: 'Amount in words',
        customerSign: "Customer's signature",
        sellerSign: "Seller's signature",
    },
};

/** The five columns a BD memo is ruled into, in the order they are printed. */
export function defaultColumns(lang = 'bn') {
    const bn = lang !== 'en';
    return [
        { id: 'c-serial', label: bn ? 'ক্রঃনং' : 'SL', flex: 0.6, align: 'center' },
        { id: 'c-desc', label: bn ? 'বিবরণ' : 'Description', flex: 3.4, align: 'left' },
        { id: 'c-qty', label: bn ? 'পরিমাণ' : 'Qty', flex: 0.85, align: 'center' },
        { id: 'c-rate', label: bn ? 'দর' : 'Rate', flex: 0.8, align: 'right' },
        { id: 'c-amount', label: bn ? 'টাকা' : 'Amount', flex: 1.4, align: 'right' },
    ];
}

let columnCounter = 0;
export const newColumn = (label = '') => {
    columnCounter += 1;
    return { id: `c-${Date.now().toString(36)}-${columnCounter}`, label, flex: 1, align: 'center' };
};

export function defaultMemo(lang = 'bn') {
    const bn = lang !== 'en';
    return {
        template: TEMPLATES[0],
        accent: ACCENT_PRESETS[0],
        docLang: lang === 'en' ? 'en' : 'bn',
        paper: 'a4',
        // Empty means "print the standard words for this language".
        title: '',
        shop: { name: '', tagline: '', address: '', phone: '', phone2: '', email: '', logo: '' },
        // The three lines the classic pad carries around the shop name: the
        // invocation over the plate, who runs the shop, and who supplies it.
        bismillah: bn ? 'বিসমিল্লাহির রাহমানির রাহীম' : '',
        proprietor: '',
        distributor: '',
        // The faint thank-you written across the writing area.
        slogan: bn ? 'ধন্যবাদ আবার আসবেন' : 'Thank you, come again',
        // Every part of the classic header can be left off the plate.
        show: Object.fromEntries(CLASSIC_SHOW.map((key) => [key, true])),
        fields: { memoNo: true, date: true, name: true, address: true },
        columns: defaultColumns(lang),
        rows: 12,
        // Off prints the grid with column splits only — the many pads that leave
        // the writing area open so a long item can run over two lines, which is
        // how the classic pad is ruled.
        rowLines: false,
        // Off drops the splits between columns, leaving the ruled lines to be
        // written across — how a plain "বিবরণ / টাকা" slip is printed.
        columnLines: true,
        totals: { total: true, advance: false, due: false },
        showWords: true,
        showSignature: true,
        // A faint mark across the middle of the pad: the shop's own name, or its
        // logo, so a photocopied memo is obvious. `mode: 'none'` prints nothing.
        // `x` / `y` are where its middle sits on the sheet, in per cent.
        watermark: { mode: 'none', text: '', image: '', opacity: 8, size: 60, angle: -24, x: 50, y: 50 },
        footerNote: '',
    };
}

const clamp = (value, min, max, fallback) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, Math.round(n)));
};

const text = (value, max = 160) => String(value ?? '').slice(0, max);

/** Keeps stored pads safe to render: unknown keys dropped, missing ones filled. */
export function normaliseMemo(raw, lang) {
    const base = defaultMemo(lang);
    if (!raw || typeof raw !== 'object') return base;
    const columns = Array.isArray(raw.columns) && raw.columns.length >= MIN_COLUMNS
        ? raw.columns.slice(0, MAX_COLUMNS).map((col, i) => ({
            id: typeof col?.id === 'string' ? col.id : `c-${i}`,
            label: text(col?.label, 40),
            flex: Number.isFinite(Number(col?.flex)) ? Math.min(6, Math.max(0.4, Number(col.flex))) : 1,
            align: ['left', 'center', 'right'].includes(col?.align) ? col.align : 'center',
        }))
        : base.columns;
    const memo = {
        ...base,
        ...raw,
        template: TEMPLATES.includes(raw.template) ? raw.template : base.template,
        accent: /^#[0-9a-fA-F]{6}$/.test(raw.accent || '') ? raw.accent : base.accent,
        docLang: raw.docLang === 'en' ? 'en' : 'bn',
        paper: PAPER_IDS.includes(raw.paper) ? raw.paper : base.paper,
        title: text(raw.title, 60),
        footerNote: text(raw.footerNote, 200),
        bismillah: text(raw.bismillah, 80),
        proprietor: text(raw.proprietor, 80),
        distributor: text(raw.distributor, 120),
        slogan: text(raw.slogan, 60),
        shop: { ...base.shop, ...(raw.shop || {}) },
        show: { ...base.show, ...(raw.show || {}) },
        fields: { ...base.fields, ...(raw.fields || {}) },
        totals: { ...base.totals, ...(raw.totals || {}) },
        watermark: {
            ...base.watermark,
            ...(raw.watermark || {}),
            // 'logo' is what an earlier version called the picture mode.
            mode: WATERMARK_MODES.includes(raw.watermark?.mode) ? raw.watermark.mode
                : raw.watermark?.mode === 'logo' ? 'image' : base.watermark.mode,
            image: typeof raw.watermark?.image === 'string' ? raw.watermark.image : base.watermark.image,
            text: text(raw.watermark?.text, 60),
            opacity: clamp(raw.watermark?.opacity, 2, 40, base.watermark.opacity),
            size: clamp(raw.watermark?.size, 20, 100, base.watermark.size),
            angle: clamp(raw.watermark?.angle, -90, 90, base.watermark.angle),
            x: clamp(raw.watermark?.x, 0, 100, base.watermark.x),
            y: clamp(raw.watermark?.y, 0, 100, base.watermark.y),
        },
        rows: clamp(raw.rows, MIN_ROWS, MAX_ROWS, base.rows),
        rowLines: typeof raw.rowLines === 'boolean' ? raw.rowLines : base.rowLines,
        columnLines: raw.columnLines !== false,
        columns,
    };
    // An earlier version let the family be chosen; the pad now prints in one face.
    delete memo.font;
    return memo;
}

export function loadDraft(lang, key) {
    try {
        const raw = window.localStorage.getItem(key);
        return normaliseMemo(raw ? JSON.parse(raw) : null, lang);
    } catch {
        return defaultMemo(lang);
    }
}

export function saveDraft(memo, key) {
    try {
        window.localStorage.setItem(key, JSON.stringify(memo));
        return true;
    } catch {
        return false;
    }
}

/** Shrinks a picked logo so the saved pad stays small. */
export function readLogoFile(file, maxSide = 360) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
                const canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
                canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
                canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/png'));
            };
            img.src = String(reader.result);
        };
        reader.readAsDataURL(file);
    });
}

/** Bangladesh mobile (01XXXXXXXXX, with or without +88) or any international number. */
/** ০১৭… typed in Bangla is the same number as 017…, so it is read as one. */
export const toEnDigits = (value) => String(value ?? '').replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d));

/** What a phone field may hold: the digits of either script, and the separators. */
export const PHONE_CHARS = /[^\d০-৯+\s\-()]/g;

export function isValidPhone(value) {
    const digits = toEnDigits(value).replace(/[\s\-().]/g, '');
    if (!digits) return true;
    return /^(\+?88)?01[3-9]\d{8}$/.test(digits) || /^\+?\d{7,15}$/.test(digits);
}

export function isValidEmail(value) {
    const value_ = String(value || '').trim();
    if (!value_) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value_);
}
